import { usePlacingPayment } from 'contexts/placingPayment';
import { useMemo, useState, createContext, ReactNode, useContext, useEffect } from 'react';
import { divide } from 'utils/number';
import { SplitBillService } from 'services/split-bill-service';
import { useAlert } from 'components/Alert';
import { SESSION } from 'constants/enum/session';
import useErrorMsg from 'utils/errorMessage';

interface SplitBillContextProps {
  handleClickOpen: () => void;
  handleClose: () => void;
  open: boolean;
  count: number;
  splitAmount: number;
  incrementCount: () => void;
  decrementCount: () => void;
  handleConfirm: (token: string) => void;
  handleSubmitSpiltBill: (values: Record<string, string>) => void;
  isHeadSplitBill: boolean;
  isSplitBill: boolean;
}

export const SplitBillContext = createContext({} as SplitBillContextProps);

export const useSplitBillContext = () => {
  const value = useContext(SplitBillContext);
  if (value === undefined) {
    throw new Error('useSplitBillContext must be used within a self Provider');
  }
  return value;
};

interface Props {
  children: ReactNode;
}

const SplitBillProvier = ({ children }: Props) => {
  const { paymentInfo, isHeadSplitBill, isSplitBill } = usePlacingPayment();
  const splitBillService = new SplitBillService();
  const [count, setCount] = useState<number>(2);
  const [open, setOpen] = useState<boolean>(false);
  const token = sessionStorage?.getItem(SESSION.TOKEN) ?? '';
  const { alert } = useAlert();
  const paymentUrl = process.env.NX_PAYMENT_FRONTEND_URL || 'http://localhost:3001';
  const { handleErrorMessageAlert } = useErrorMsg();

  useEffect(() => {
    isHeadSplitBill && sessionStorage?.setItem(SESSION.HEAD_SPLIT_TOKEN, token);
  }, [isHeadSplitBill, token]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmitSpiltBill = (values: Record<string, string>) => {
    setOpen(false);
    handleConfirm(token);
  };

  const splitAmount = useMemo(() => {
    return count === 0 ? paymentInfo.amount : divide(paymentInfo.fullAmount, count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, paymentInfo.amount]);

  const incrementCount = () => {
    setCount((value) => value + 1);
  };

  const decrementCount = () => {
    setCount((value) => Math.max(value - 1, 2));
  };

  const handleConfirm = (token: string) => {
    splitBillService
      .updateTotalSplit(token, count)
      .then((item) => {
        const url = `${paymentUrl}/${item.token}`;
        window.opener = null;
        window.open(url, '_self');
      })
      .catch((error) => {
        alert(handleErrorMessageAlert(error), {
          variant: 'error',
        });
      });
  };

  const value = {
    open,
    handleClickOpen,
    handleClose,
    splitAmount,
    incrementCount,
    decrementCount,
    handleConfirm,
    handleSubmitSpiltBill,
    count,
    isHeadSplitBill,
    isSplitBill,
  };

  return <SplitBillContext.Provider value={value}>{children}</SplitBillContext.Provider>;
};

export default SplitBillProvier;
